import api from './api'
import {
  MonthlyScore,
  CreateScoreRequest,
  QuarterlySummary,
  QuarterlyChartData,
  CheckExistingResponse,
  AccumulatedChartData,
  SelfAssessmentRequest,
  ReviewScoreRequest,
  PendingCountResponse,
  MyAssessmentStatusResponse,
  SelfAssessmentSettingResponse,
} from './types'

export const scoresService = {
  /**
   * Create a new monthly score
   */
  async create(data: CreateScoreRequest): Promise<MonthlyScore> {
    const response = await api.post<MonthlyScore>('/scores/', data)
    return response.data
  },

  /**
   * Get all scores
   */
  async getAll(params?: {
    employee?: number
    month?: number
    year?: number
    is_reversed?: boolean
  }): Promise<MonthlyScore[]> {
    const response = await api.get<MonthlyScore[]>('/scores/', { params })
    return response.data
  },

  /**
   * Get score by ID
   */
  async getById(id: number): Promise<MonthlyScore> {
    const response = await api.get<MonthlyScore>(`/scores/${id}/`)
    return response.data
  },

  /**
   * Update score
   */
  async update(id: number, data: Partial<CreateScoreRequest> & { is_reversed?: boolean }): Promise<MonthlyScore> {
    const response = await api.patch<MonthlyScore>(`/scores/${id}/`, data)
    return response.data
  },

  /**
   * Delete score
   */
  async delete(id: number): Promise<void> {
    await api.delete(`/scores/${id}/`)
  },

  /**
   * Get quarterly summary for all employees
   */
  async getQuarterlySummary(year: number, quarter: number): Promise<QuarterlySummary> {
    const response = await api.get<QuarterlySummary>('/scores/quarterly-summary/', {
      params: { year, quarter },
    })
    return response.data
  },

  /**
   * Get quarterly chart data for a specific employee
   */
  async getQuarterlyChart(employeeId: number, year: number): Promise<QuarterlyChartData> {
    const response = await api.get<QuarterlyChartData>(`/scores/${employeeId}/quarterly-chart/`, {
      params: { year },
    })
    return response.data
  },

  async checkExisting(employee: number, month: number, year: number): Promise<CheckExistingResponse> {
    const response = await api.get<CheckExistingResponse>('/scores/check-existing/', {
      params: { employee, month, year },
    })
    return response.data
  },

  async getAccumulatedChart(employeeId: number): Promise<AccumulatedChartData> {
    const response = await api.get<AccumulatedChartData>(`/scores/${employeeId}/accumulated-chart/`)
    return response.data
  },

  async selfAssess(data: SelfAssessmentRequest): Promise<MonthlyScore> {
    const response = await api.post<MonthlyScore>('/scores/self-assess/', data)
    return response.data
  },

  async review(id: number, data: ReviewScoreRequest): Promise<MonthlyScore> {
    const response = await api.post<MonthlyScore>(`/scores/${id}/review/`, data)
    return response.data
  },

  async getPendingCount(): Promise<PendingCountResponse> {
    const response = await api.get<PendingCountResponse>('/scores/pending-count/')
    return response.data
  },

  async getMyAssessmentStatus(month?: number, year?: number): Promise<MyAssessmentStatusResponse> {
    const response = await api.get<MyAssessmentStatusResponse>('/scores/my-assessment-status/', {
      params: { ...(month && { month }), ...(year && { year }) },
    })
    return response.data
  },

  async getSelfAssessmentSetting(): Promise<SelfAssessmentSettingResponse> {
    const response = await api.get<SelfAssessmentSettingResponse>('/scores/self-assessment-setting/')
    return response.data
  },

  async setSelfAssessmentSetting(is_enabled: boolean): Promise<SelfAssessmentSettingResponse> {
    const response = await api.post<SelfAssessmentSettingResponse>('/scores/self-assessment-setting/', { is_enabled })
    return response.data
  },

  async exportScores(year: number): Promise<void> {
    const response = await api.get('/scores/export/', {
      params: { year },
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `pontuacao_${year}.xlsx`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  },
}

export default scoresService
